// `.task-item` del mock `wizard/09-entrega.html`: checklist con estado activo / listo / bloqueado y
// cuerpo desplegable. Lo comparten las dos pistas del paso 09.
import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';

export type TaskState = 'active' | 'done' | 'blocked' | 'failed';

const STATUS_LABEL: Record<TaskState, string> = {
    done: 'Listo',
    active: 'Activo',
    blocked: '—',
    failed: 'Rechazado',
};

export function TaskItem({
    n,
    state,
    title,
    desc,
    alwaysOpen = false,
    children,
}: {
    n: number;
    state: TaskState;
    title: string;
    desc: string;
    /** Deja el cuerpo a la vista también en `done`, para lo que hay que seguir leyendo ya cumplido. */
    alwaysOpen?: boolean;
    children: ReactNode;
}) {
    const statusLabel = STATUS_LABEL[state];

    return (
        <section
            className={cn(
                'flex items-start gap-3 rounded-[10px] border-[1.5px] bg-card px-4 py-3.5 transition-colors',
                state === 'active' &&
                    'border-primary bg-accent/50 shadow-[0_0_0_3px_hsl(var(--primary)/0.08)]',
                state === 'done' && 'border-success-soft-border bg-success-soft/50',
                state === 'blocked' && 'border-border opacity-50',
                state === 'failed' && 'border-destructive/60 bg-destructive/5',
            )}
        >
            <span
                className={cn(
                    'flex size-7 flex-none items-center justify-center rounded-full border-[1.5px] text-[12px] font-bold',
                    state === 'active' && 'border-primary bg-primary text-primary-foreground',
                    state === 'done' && 'border-success bg-success text-success-foreground',
                    state === 'blocked' && 'border-border bg-muted text-foreground/70',
                    state === 'failed' &&
                        'border-destructive bg-destructive text-destructive-foreground',
                )}
            >
                {state === 'done' ? <Check className="size-3.5" strokeWidth={3} aria-hidden /> : n}
            </span>

            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[13.5px] font-semibold leading-[1.2]">{title}</div>
                        <div className="mt-0.5 text-[11.5px] leading-[1.4] text-muted-foreground">
                            {desc}
                        </div>
                    </div>
                    <div
                        className={cn(
                            'flex-none text-[10.5px] font-semibold uppercase tracking-[0.04em]',
                            state === 'done' && 'text-success',
                            state === 'active' && 'text-primary',
                            state === 'blocked' && 'text-muted-foreground',
                            state === 'failed' && 'text-destructive',
                        )}
                    >
                        {statusLabel}
                    </div>
                </div>

                {(state === 'active' || state === 'failed' || (alwaysOpen && state === 'done')) && (
                    <div className="mt-3 border-t border-dashed border-border pt-3">{children}</div>
                )}
            </div>
        </section>
    );
}
